import { useEffect, useState, useCallback } from "react";
import { Loader2, PlayCircle, AlertTriangle, Users, Settings2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/common/DataTable";
import { TooltipButton } from "@/components/common/Tooltips";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import UltimaCorridaCard from "./UltimaCorridaCard";

// PLD Bloque 1 · Pieza 1 — Panel de monitoreo de perfiles de la entidad (43ª-V / 21ª).
// Corre el barrido masivo, parametriza el % de desviación y lista las alertas generadas.
const MONITOR_ROLES = ["admin_entidad", "oficial_cumplimiento", "analista_pld"];
const CONFIG_ROLES = ["admin_entidad", "oficial_cumplimiento"];

function Kpi({ label, value, icon: Icon, tone = "text-primary" }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><Icon className={`h-4 w-4 ${tone}`} /> {label}</div>
      <p className="mt-1 font-heading text-2xl font-semibold text-slate-800">{value}</p>
    </div>
  );
}

export default function MonitoreoPerfil() {
  const { user } = useAuth();
  const canMonitor = MONITOR_ROLES.includes(user?.role);
  const canConfig = CONFIG_ROLES.includes(user?.role);
  const [data, setData] = useState({ alertas: [], total_abiertas: 0, clientes_con_perfil: 0, perfiles_calculados: 0 });
  const [cfg, setCfg] = useState({ desviacion_monto_pct: 30, desviacion_ops_pct: 50, meses_analisis: 12 });
  const [busy, setBusy] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [corridaRefresh, setCorridaRefresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([
        api.get("/pld/monitoreo/desviaciones").then((r) => r.data),
        api.get("/pld/config").then((r) => r.data),
      ]);
      setData(d); setCfg(c);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const correr = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pld/monitoreo/perfil/correr");
      toast.success(`Monitoreo: ${data.analizados} clientes · ${data.desviaciones} desviaciones · ${data.alertas_generadas} alertas nuevas`,
        { description: `Constancia ${data.constancias?.perfil?.folio} emitida y sellada.` });
      setCorridaRefresh((v) => v + 1);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo correr el monitoreo"); }
    finally { setBusy(false); }
  };

  const guardarCfg = async () => {
    setSavingCfg(true);
    try {
      const { data } = await api.put("/pld/config", {
        desviacion_monto_pct: Number(cfg.desviacion_monto_pct),
        desviacion_ops_pct: Number(cfg.desviacion_ops_pct),
        meses_analisis: Number(cfg.meses_analisis),
      });
      setCfg(data);
      toast.success("Umbrales de desviación actualizados");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar la configuración"); }
    finally { setSavingCfg(false); }
  };

  return (
    <div className="space-y-5" data-testid="monitoreo-perfil">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm text-slate-500">
          Compara la operación real de cada cliente contra su perfil declarado (Disposición 21ª) y genera
          alertas de posible operación inusual por desviación (43ª-V) para el Oficial de Cumplimiento. No bloquea.
        </p>
        {canMonitor && (
          <TooltipButton tooltip="Correr el motor de monitoreo transaccional del periodo"
            data-testid="correr-monitoreo-btn" onClick={correr} disabled={busy} className="bg-accent hover:bg-forest-700">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-1.5 h-4 w-4" />} Correr monitoreo de perfiles
          </TooltipButton>
        )}
      </div>

      <UltimaCorridaCard modulo="perfil" refreshKey={corridaRefresh} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Clientes con perfil esperado" value={data.clientes_con_perfil} icon={Users} />
        <Kpi label="Clientes evaluados" value={data.perfiles_calculados} icon={Users} />
        <Kpi label="Desviaciones abiertas" value={data.total_abiertas} icon={AlertTriangle} tone="text-amber-600" />
        <Kpi label="Umbral de desviación (monto)" value={`${cfg.desviacion_monto_pct}%`} icon={Settings2} />
      </div>

      {canConfig && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="config-desviacion">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Parámetros de desviación</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="mb-1 block text-xs">% sobre el monto estimado</Label>
              <Input data-testid="cfg-monto-pct" type="number" className="w-32" value={cfg.desviacion_monto_pct} onChange={(e) => setCfg((p) => ({ ...p, desviacion_monto_pct: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">% sobre operaciones estimadas</Label>
              <Input data-testid="cfg-ops-pct" type="number" className="w-32" value={cfg.desviacion_ops_pct} onChange={(e) => setCfg((p) => ({ ...p, desviacion_ops_pct: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Meses de análisis</Label>
              <Input type="number" className="w-28" value={cfg.meses_analisis} onChange={(e) => setCfg((p) => ({ ...p, meses_analisis: e.target.value }))} />
            </div>
            <Button data-testid="guardar-cfg-btn" size="sm" variant="outline" onClick={guardarCfg} disabled={savingCfg}>
              {savingCfg ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar
            </Button>
          </div>
        </div>
      )}

      <DataTable testid="table-desviaciones"
        rows={data.alertas}
        columns={[
          { key: "folio", label: "Folio" },
          { key: "fecha", label: "Fecha", kind: "date" },
          { key: "cliente", label: "Cliente" },
          { key: "nivel_riesgo", label: "Nivel", kind: "risk" },
          { key: "descripcion", label: "Detalle de la desviación" },
          { key: "estatus", label: "Estatus", kind: "badge" },
        ]}
        emptyLabel="Sin alertas por desviación. Corre el monitoreo para evaluar a los clientes." />
    </div>
  );
}
